import * as React from 'react';
import {Loader2} from 'lucide-react';
import {useTranslation} from 'react-i18next';
import {FieldErrorMessage} from '../../field-error-message';
import {HelpTooltip} from '../../help-tooltip';
import {cn} from '../../lib/cn';
import {AirbnbFieldHelperText} from '../field-helper-text';

type AirbnbFieldTriggerProps = {
  as?: 'button' | 'div';
  id: string;
  label: string;
  topLabel?: string;
  labelId: string;
  valueId?: string;
  helperTextId?: string;
  errorId?: string;
  labelText?: React.ReactNode;
  valueText?: string;
  placeholder?: string;
  helper?: React.ReactNode;
  disabled?: boolean;
  error?: boolean | string;
  loading?: boolean;
  optional?: boolean | string;
  tooltip?: React.ReactNode;
  describedBy?: string;
  className?: string;
  contentClassName?: string;
  trailingAdornment?: React.ReactNode;
  forceFloatingLabel?: boolean;
  forceLabelText?: boolean;
  hideErrorMessage?: boolean;
  children?: React.ReactNode;
  onClick?: () => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement | HTMLDivElement>;
} & Omit<
  React.HTMLAttributes<HTMLButtonElement | HTMLDivElement>,
  'children' | 'className' | 'disabled' | 'id' | 'onClick' | 'onKeyDown'
>;

const AirbnbFieldTrigger = React.forwardRef<
  HTMLButtonElement | HTMLDivElement,
  AirbnbFieldTriggerProps
>(
  (
    {
      as = 'button',
      id,
      label,
      topLabel,
      labelId,
      valueId,
      helperTextId,
      errorId,
      labelText,
      valueText,
      placeholder,
      helper,
      disabled,
      error,
      loading,
      optional,
      tooltip,
      describedBy,
      className,
      contentClassName,
      trailingAdornment,
      forceFloatingLabel = false,
      forceLabelText = false,
      hideErrorMessage = false,
      children,
      onClick,
      onKeyDown,
      ...props
    },
    ref,
  ) => {
    const {t} = useTranslation();
    const generatedHelperId = React.useId();
    const hasValue = Boolean(valueText);
    const isRaised = hasValue || forceFloatingLabel;
    const optionalLabel = optional
      ? typeof optional === 'string'
        ? optional
        : t('optional')
      : undefined;
    const visibleLabelText = labelText ?? label;
    const hasLabelMeta = Boolean(optionalLabel) || Boolean(tooltip);
    const resolvedLabelText =
      visibleLabelText && hasLabelMeta ? (
        <span className="inline-flex max-w-full items-center gap-1.5 align-middle">
          <span className="min-w-0 truncate">{visibleLabelText}</span>
          {optionalLabel && (
            <span className="text-current opacity-70 lowercase">({optionalLabel})</span>
          )}
          {tooltip && (
            <HelpTooltip
              content={tooltip}
              side="top"
              size={16}
              triggerAs={as === 'button' ? 'span' : 'button'}
              className="pointer-events-auto text-current opacity-70 hover:text-current hover:opacity-100"
            />
          )}
        </span>
      ) : (
        visibleLabelText
      );
    const animatedLabel = forceLabelText
      ? (resolvedLabelText ?? placeholder)
      : isRaised
        ? resolvedLabelText
        : (label ?? placeholder);
    const hasInvalidState = Boolean(error);
    const errorMessage = typeof error === 'string' ? error : undefined;
    const showErrorMessage = Boolean(errorMessage) && !hideErrorMessage;
    const showHelper = Boolean(helper) && !showErrorMessage;
    const helperId = helper ? generatedHelperId : undefined;
    const describedByValue =
      [describedBy, showHelper ? helperId : undefined].filter(Boolean).join(' ') ||
      undefined;
    const isBlocked = Boolean(disabled) || Boolean(loading);
    const resolvedTrailingAdornment =
      loading || trailingAdornment ? (
        <span className="flex items-center gap-2">
          {trailingAdornment}
          {loading && (
            <Loader2
              aria-hidden="true"
              className="h-5 w-5 animate-spin text-[var(--chekin-color-gray-1)]"
            />
          )}
        </span>
      ) : undefined;
    const sharedClasses = cn(
      'relative flex w-full items-center border bg-transparent text-left transition-colors focus-visible:outline-none',
      'rounded-[12px] border-[var(--chekin-airbnb-gray-muted)] pl-4 pr-4 focus-visible:ring-2 focus-visible:ring-[var(--chekin-airbnb-ink-strong)] focus-visible:ring-offset-2',
      isRaised ? 'min-h-[60px]' : 'h-[60px]',
      hasInvalidState
        ? 'border-[var(--error-message-color)] bg-[var(--chekin-airbnb-pink-surface-soft)]'
        : 'border-[var(--chekin-airbnb-gray-muted)]',
      disabled
        ? 'cursor-not-allowed opacity-50'
        : loading
          ? 'cursor-progress'
          : 'cursor-pointer',
      className,
    );
    const sharedContent = (
      <>
        <span
          className={cn(
            'relative min-w-0 flex-1 pr-2',
            isRaised ? 'h-[42px]' : 'h-[28px]',
            contentClassName,
          )}
        >
          <span
            id={labelId}
            className={cn(
              'absolute left-0 origin-left transition-all duration-200 ease-out',
              hasLabelMeta ? 'max-w-full' : 'pointer-events-none truncate',
              isRaised
                ? 'top-[-1px] translate-y-0 text-xs leading-5'
                : 'top-1/2 -translate-y-1/2 text-[16px] leading-7',
              hasInvalidState
                ? 'text-[var(--error-message-color)]'
                : 'text-[var(--chekin-airbnb-gray-text)]',
            )}
          >
            {animatedLabel}
          </span>
          {children ? (
            children
          ) : hasValue ? (
            <span
              id={valueId}
              className={cn(
                'absolute left-0 block truncate transition-all duration-200 ease-out',
                'bottom-0 translate-y-0 text-[16px] leading-6 opacity-100',
                hasInvalidState
                  ? 'text-[var(--error-message-color)]'
                  : 'text-[var(--chekin-airbnb-ink-strong)]',
              )}
            >
              {valueText}
            </span>
          ) : (
            <span id={helperTextId} className="sr-only">
              {placeholder ?? label}
            </span>
          )}
        </span>
        {resolvedTrailingAdornment && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2"
          >
            {resolvedTrailingAdornment}
          </span>
        )}
      </>
    );

    return (
      <div className="w-full">
        {topLabel && (
          <p className="mb-3 text-[16px] font-semibold leading-5 text-[var(--chekin-airbnb-ink-strong)]">
            {topLabel}
          </p>
        )}
        {as === 'button' ? (
          <button
            id={id}
            ref={ref as React.ForwardedRef<HTMLButtonElement>}
            type="button"
            aria-labelledby={hasValue && valueId ? `${labelId} ${valueId}` : labelId}
            aria-describedby={describedByValue}
            aria-invalid={hasInvalidState}
            aria-busy={loading}
            disabled={isBlocked}
            onClick={isBlocked ? undefined : onClick}
            onKeyDown={
              isBlocked
                ? undefined
                : (onKeyDown as React.KeyboardEventHandler<HTMLButtonElement>)
            }
            className={sharedClasses}
            {...(props as Omit<
              React.ButtonHTMLAttributes<HTMLButtonElement>,
              | 'children'
              | 'className'
              | 'disabled'
              | 'id'
              | 'onClick'
              | 'onKeyDown'
              | 'type'
            >)}
          >
            {sharedContent}
          </button>
        ) : (
          <div
            id={id}
            ref={ref as React.ForwardedRef<HTMLDivElement>}
            aria-labelledby={hasValue && valueId ? `${labelId} ${valueId}` : labelId}
            aria-describedby={describedByValue}
            aria-invalid={hasInvalidState}
            aria-busy={loading}
            aria-disabled={isBlocked}
            onClick={isBlocked ? undefined : onClick}
            onKeyDown={
              isBlocked
                ? undefined
                : (onKeyDown as React.KeyboardEventHandler<HTMLDivElement>)
            }
            className={sharedClasses}
            {...(props as Omit<
              React.HTMLAttributes<HTMLDivElement>,
              'children' | 'className' | 'id' | 'onClick' | 'onKeyDown'
            >)}
          >
            {sharedContent}
          </div>
        )}
        {showErrorMessage && <FieldErrorMessage id={errorId} message={errorMessage} />}
        {showHelper && (
          <AirbnbFieldHelperText id={helperId} helper={helper} disabled={disabled} />
        )}
      </div>
    );
  },
);

AirbnbFieldTrigger.displayName = 'AirbnbFieldTrigger';

export {AirbnbFieldTrigger};
export type {AirbnbFieldTriggerProps};
